import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  apiClient,
  type BrandOut,
  type MentionQuery,
} from "@/shared/api/api-client";
import {
  APPROACH_CHOICES,
  CATALOG_DIMENSIONS,
  DIMENSIONS,
  DIMENSION_LABELS,
  FORMAT_CHOICES,
  ID_DIMENSIONS,
  MEDIA_TYPE_CHOICES,
  NO_VALUE,
  SOURCE_CHOICES,
  TIER_CHOICES,
  idsToSend,
  isIdDimension,
  resolveFacetIds,
  type IdDimension,
  type SavedFacetIds,
  dropThemesOfGroups,
  emptyFilters,
  sanitizeMinAdValue,
  stateFromCriteria,
  type Dimension,
  type MentionFilterCriteria,
  type MentionFilters,
} from "@/features/mentions/mention-filter-criteria";
import {
  EMPTY_FILTER_OPTIONS,
  acrossOwners,
  byName,
  catalogRows,
  loadFilterOptions,
  mergeNamedCatalog,
  ownersForScope,
  type FilterOptions,
} from "@/features/mentions/filter-options";
import {
  emptyRecorte,
  mentionCriteriaFromGeneration,
  mentionCriteriaFromRecorte,
  noValueTravels,
  PROVISIONAL_LABEL,
  RECORTE_DIMENSIONS,
  type ReportRecorte,
} from "@/features/reports/report-recorte";
import {
  useMentionFilterStore,
  type MentionListScope,
  type PastedReferences,
} from "@/features/mentions/mention-filter-context";
import { useSummaryDays } from "@/shared/components/SummaryPeriodPicker";
import { shiftDays, todayInInstallation } from "@/shared/utils/installation-clock";
import { SUMMARY_PERIODS, type SummaryPeriod } from "@/shared/utils/summary-window";
import type {
  GenerationCriteria,
  GenerationCriteriaLabels,
} from "@/shared/api/api-client";
import { formatCurrencyFull } from "@/shared/utils/format-currency";
import { localInstant } from "@/shared/utils/date-range";
import { polarityOptions, type PolarityScale } from "@/shared/types/polarity";
import { errorMessage } from "@/shared/api/http";
import {
  isMentionOrder,
  MENTION_ORDER_DEFAULT,
  type MentionOrder
} from "@/shared/vocabulary/mention-orders";
import type { MentionSearchField } from "@/shared/vocabulary/mention-search-fields";

/**
 * O RECORTE da tela de menções: filtros vivos, o rascunho do painel
 * "Mais filtros", busca, escopo, ordem, página, lista colada e a
 * montagem da consulta (`buildQuery`/`queryKey`).
 *
 * Extraído da `MentionsPage` (2026-10-09) sem mudar comportamento: é o
 * que decide QUANTAS consultas a tela faz, e a página não tinha teste
 * de render que o dissesse. Aqui ele é testável com `renderHook`
 * (`use-mention-filters.test.ts`). A página fica com a lista, a
 * seleção, o lote, o reprocessamento e o JSX.
 */

/** As dimensões cujo catálogo muda com o escopo (cada empresa tem o
 *  seu). Grupo de publicador, porte e canal são lista única do cliente. */
const SCOPED_CATALOG_DIMENSIONS = [
  "brands",
  "themes",
  "themeGroups",
  "spokespersons",
  "keyMessages",
] as const;

/** A validade de uma lista colada (7 dias, a do servidor): uma lista que
 *  veio da URL não diz quando vence, e o servidor é quem decide. */
const REFERENCE_SET_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// Chave separada do layout (Lista/Cards): são duas preferências
// independentes — a fonte dos dados e como eles são desenhados.
const MENTIONS_SOURCE_KEY = "reputaition.mentions_source";
// A ordem da lista (2026-10-02). Preferência de APRESENTAÇÃO, como o
// modo de visualização: fica no navegador, e FORA do filtro salvo e do
// recorte do relatório de propósito — ordenar não muda o que entra.
const MENTIONS_ORDER_KEY = "reputaition.mentions_order";

// Os tipos moram na loja (que é quem os persiste); reexportados daqui
// pra não quebrar quem sempre os importou do hook.
export type { MentionListScope, PastedReferences };

function savedOrder(): MentionOrder {
  try {
    const saved = window.localStorage.getItem(MENTIONS_ORDER_KEY);
    return isMentionOrder(saved) ? saved : MENTION_ORDER_DEFAULT;
  } catch {
    return MENTION_ORDER_DEFAULT;
  }
}

/** Assinatura estável de um estado de filtros, pra comparar rascunho e
 *  vivo: `Set` vira lista ordenada, o resto já é dado puro. */
function filtersSignature(state: MentionFilters): string {
  return JSON.stringify(state, (_key, value) =>
    value instanceof Set ? [...value].sort() : value
  );
}

type UseMentionFiltersOptions = {
  token: string;
  /** Filtro "só as menções destes uploads" — mora na URL; quem muda é a
   *  rota. */
  uploadFilter: number[];
  onChangeUploadFilter?: (uploadIds: number[]) => void;
  /** Vem de `useShowsCena()`: com o CENA oculto, o filtro por CENA some
   *  da consulta e do contador. */
  showsCena: boolean;
  /** Vem de `usePolarityScale()`: o filtro de polaridade oferece só as
   *  polaridades da escala da empresa (decisão da dona, 2026-10-18 —
   *  "neutro só pode aparecer se tiver na config da empresa"; desfaz,
   *  SÓ pro filtro, o "filtro é busca, não classificação" de
   *  2026-08-14). Sem default de propósito: um chamador novo que
   *  esquecesse o eixo mostraria Neutro em toda empresa de 4 níveis. */
  polarityScale: PolarityScale;
  /** O rótulo de um upload pro chip do filtro de upload; sem ele (ou
   *  sem rótulo conhecido) o chip diz `#id`. */
  uploadLabelOf?: (id: number) => string | undefined;
};

export function useMentionFilters({
  token,
  uploadFilter,
  onChangeUploadFilter,
  showsCena,
  polarityScale,
  uploadLabelOf,
}: UseMentionFiltersOptions) {
  // O filtro mora na LOJA da casca (2026-10-17): é compartilhado com o
  // formulário do relatório e sobrevive a trocar de rota. O que fica
  // aqui é da tela — rascunho do painel, página, ordem, catálogo.
  const store = useMentionFilterStore();
  const setShared = store.setState;
  const { filters, query, searchIn, scope, refFilter, savedIds } = store.state;
  const setQuery = useCallback(
    (next: string) => setShared(current => ({ ...current, query: next })),
    [setShared]
  );
  const setSearchIn = useCallback(
    (next: MentionSearchField) =>
      setShared(current => ({ ...current, searchIn: next })),
    [setShared]
  );
  const setScope = useCallback(
    (next: MentionListScope) =>
      setShared(current => ({ ...current, scope: next })),
    [setShared]
  );
  const setRefFilter = useCallback(
    (next: PastedReferences | null) =>
      setShared(current => ({ ...current, refFilter: next })),
    [setShared]
  );
  const setFilters = useCallback(
    (next: MentionFilters | ((current: MentionFilters) => MentionFilters)) =>
      setShared(current => ({
        ...current,
        filters: typeof next === "function" ? next(current.filters) : next,
      })),
    [setShared]
  );
  // Semeado do que a loja já tem: o filtro restaurado (ou o que a outra
  // tela deixou) não pode custar uma consulta SEM a busca e outra COM
  // ela, 350 ms depois.
  const [debouncedQuery, setDebouncedQuery] = useState(() => query.trim());
  // `searchIn` (2026-10-14): onde a busca procura. Mora AO LADO da caixa
  // de busca e não no rascunho do painel: o seletor é vizinho da caixa,
  // que é imediata, e não precisa de debounce (escolher uma opção não é
  // digitar). Só vale com texto — ver `searchInSent`.
  // O que a consulta de fato leva: sem texto o campo não significa nada,
  // então trocar o seletor com a caixa vazia NÃO muda a chave do recorte
  // nem dispara consulta (a mesma regra do servidor, que força `all`).
  const searchInSent: MentionSearchField = debouncedQuery ? searchIn : "all";
  // `filters` é o recorte VIVO (na loja). O painel "Mais filtros" edita um RASCUNHO, e só o "Aplicar" o
  // promove a `filters` (que é o que a consulta enxerga). Antes cada
  // controle escrevia direto no estado vivo e disparava uma consulta:
  // marcar 4 temas eram 4 consultas, e digitar o ano numa data eram 4
  // (0002, 0020, 0202, 2026) — medido, numa rede de 17 kB/s. O rascunho
  // é semeado de `filters` a cada abertura; fechar pelo X o descarta.
  const [draft, setDraft] = useState<MentionFilters>(emptyFilters);
  // Tudo o que escreve no estado VIVO com o painel aberto (chips,
  // filtro salvo, escopo, "Limpar") espelha no rascunho, senão o painel
  // mostraria um rascunho velho do que a tela já mudou. `updateFilters`
  // aplica a mesma mudança aos dois (preserva o que a pessoa já editou
  // no rascunho e ainda não aplicou); `commitFilters` substitui os dois.
  const updateFilters = (change: (current: MentionFilters) => MentionFilters) => {
    setFilters(change);
    setDraft(change);
  };
  const commitFilters = (next: MentionFilters) => {
    setFilters(next);
    setDraft(next);
  };
  // O catálogo das opções de filtro. Carregado UMA vez, na primeira
  // abertura do painel — são seis rotas de catálogo, e ninguém abre o
  // painel uma vez só.
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(
    EMPTY_FILTER_OPTIONS
  );
  // `optionsLoaded` é "a carga JÁ FOI DISPARADA" (evita disparar duas
  // vezes); `optionsReady` é "as opções chegaram". A distinção importa
  // porque as dimensões de CATÁLOGO viram id pelo `idsByName`, e antes
  // de chegar ele está vazio — ver `catalogFacetsSelected` abaixo.
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [optionsReady, setOptionsReady] = useState(false);
  //: Qual carga de opções é a corrente. Ver o efeito que a usa.
  const optionsRequestRef = useRef(0);

  const [filtersOpen, setFiltersOpen] = useState(false);
  // A barra de filtros rápidos (2026-10-30) pede o catálogo quando tem
  // um filtro de catálogo nela — sem isso Tema, Canal etc. abririam
  // vazios na barra até alguém abrir o painel.
  const [optionsWanted, setOptionsWanted] = useState(false);
  const requestFilterOptions = useCallback(() => setOptionsWanted(true), []);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // A JANELA DE DATA PADRÃO (2026-10-26). Na primeira visita da empresa
  // neste navegador (a loja ainda não existe) a tela abre com os últimos
  // N dias do ÚLTIMO DADO, como a Visão geral — N é a escolha lembrada
  // dela (`useSummaryDays`). Só a primeira visita: depois vale o que a
  // pessoa fez, inclusive esvaziar o período no painel. Também não entra com
  // `?upload=` (o upload pode ser antigo) nem quando um filtro salvo é
  // aplicado (ato explícito, que traz o período que tiver).
  //
  // **Enquanto a resposta não chega a consulta NÃO sai** (`waitingForWindow`
  // entra no `waitingForCatalog` devolvido, a mesma forma): sem isso a
  // primeira consulta sairia sem janela e a segunda, com ela. Se a rota
  // falha, a lista abre sem janela e sem erro (fail-open), e a loja
  // segue "nova" — a próxima visita tenta de novo.
  const [summaryDays, setSummaryDays] = useSummaryDays();
  const summaryDaysRef = useRef<SummaryPeriod>(summaryDays);
  summaryDaysRef.current = summaryDays;
  const freshRef = useRef(store.fresh);
  freshRef.current = store.fresh;
  const [waitingForWindow, setWaitingForWindow] = useState(
    () => store.fresh && uploadFilter.length === 0
  );
  // O dia do último dado, quando já foi buscado (`undefined` = não
  // buscou). Em estado, e não só em ref, porque o botão ativo depende
  // dele.
  const [latestDay, setLatestDay] = useState<string | undefined>(undefined);
  const latestDayRef = useRef<Promise<string | null> | null>(null);

  /** Busca (uma vez) o dia do último dado; sem dado nenhum, hoje na
   *  instalação. `null` = a rota falhou. */
  const fetchLatestDay = useCallback((): Promise<string | null> => {
    if (!latestDayRef.current) {
      latestDayRef.current = apiClient
        .latestPublished(token, { scope })
        .then((response) => {
          const day = response.latest_published_at
            ? response.latest_published_at.slice(0, 10)
            : todayInInstallation();
          setLatestDay(day);
          return day;
        })
        .catch(() => {
          // Deixa a próxima tentativa buscar de novo.
          latestDayRef.current = null;
          return null;
        });
    }
    return latestDayRef.current;
  }, [token, scope]);

  const windowOf = (lastDay: string, days: number) => ({
    dateFrom: shiftDays(lastDay, -(days - 1)),
    dateTo: lastDay,
    timeFrom: "",
    timeTo: "",
  });

  useEffect(() => {
    if (!waitingForWindow) return;
    let current = true;
    void fetchLatestDay().then((day) => {
      if (!current) return;
      // Alguém escreveu na loja enquanto a resposta vinha (um filtro
      // salvo, um recorte de relatório): o que a pessoa fez vale.
      if (day !== null && freshRef.current) {
        updateFilters((filters) => ({
          ...filters,
          ...windowOf(day, summaryDaysRef.current),
        }));
        setPage(1);
      }
      setWaitingForWindow(false);
    });
    return () => {
      current = false;
    };
    // Roda na montagem: o estado inicial já diz se há o que esperar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [brands, setBrands] = useState<BrandOut[]>([]);

  // A FONTE dos dados, não o layout: "brand" é uma linha por (post ×
  // marca) — a lista de sempre; "publication" é uma linha por matéria,
  // com as marcas citadas nela numa coluna. A mesma matéria citando 3
  // marcas aparece 3 vezes no primeiro modo, e uma vez no segundo.
  const [sourceMode, setSourceMode] = useState<"brand" | "publication">(
    "brand",
  );
  // Inicializa do storage no PRIMEIRO render, e não num efeito: lida
  // depois, a ordem salva disparava uma segunda consulta logo após a
  // primeira, com a lista piscando na ordem errada no meio.
  const [order, setOrder] = useState<MentionOrder>(savedOrder);

  // `scope` (na loja): De QUEM são as menções da lista (2026-10-03): a
  // empresa (padrão), os concorrentes dela, ou todas. O servidor sempre
  // soube responder os três; a tela só pedia o primeiro, então menção de
  // concorrente nunca aparecia — nem a que a esteira criava, nem a do
  // vínculo manual. Fica FORA do filtro salvo (decisão de 2026-08-26: o
  // escopo sai do usuário), mas VIAJA pro relatório (2026-10-04) e agora
  // é compartilhado com ele pela loja.
  // O seletor só aparece pra quem TEM concorrente cadastrado. Os ids
  // (do VÍNCULO) também dizem de quem é o catálogo que cada escopo
  // oferece no painel — ver `ownersForScope`.
  const [competitorIds, setCompetitorIds] = useState<number[]>([]);
  // A lista de concorrentes já respondeu? Um escopo restaurado do
  // navegador (`competitors`/`all`) precisa dela pra saber de quem é o
  // catálogo — carregar as opções antes montaria o catálogo errado.
  const [competitorsLoaded, setCompetitorsLoaded] = useState(false);
  const hasCompetitors = competitorIds.length > 0;
  // A chave dos donos do catálogo no escopo atual: com "A empresa" ela
  // não muda quando a lista de concorrentes chega, e nada recarrega.
  const ownersKey = ownersForScope(scope, competitorIds).join(",");
  useEffect(() => {
    let alive = true;
    apiClient
      .listCompetitors(token)
      .then(rows => {
        if (!alive) return;
        setCompetitorIds(rows.map(row => row.id));
        setCompetitorsLoaded(true);
        // Escopo restaurado de uma época em que havia concorrente: sem
        // nenhum cadastrado, o seletor não aparece e a pessoa ficaria
        // presa numa lista que não consegue mudar.
        if (rows.length === 0) {
          setShared(current =>
            current.scope === "own" ? current : { ...current, scope: "own" }
          );
        }
      })
      .catch(() => {
        // Sem a lista, o seletor não aparece e a tela segue na empresa.
        if (alive) setCompetitorsLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  // Os links/códigos colados: o filtro aplicado, o rascunho da caixa e
  // o estado da chamada que os resolve.
  // (`refFilter`, o filtro aplicado, mora na loja e não é gravado.)
  const [refDraft, setRefDraft] = useState("");
  const [refBusy, setRefBusy] = useState(false);
  const [refError, setRefError] = useState<string | null>(null);


  // A FONTE dos dados vem do storage no mount (a página lê o layout, que
  // é dela).
  useEffect(() => {
    // Mesma guarda da ordem (`savedOrder`): sem storage (janela privada,
    // cota) a visão vale só nesta aba — até 2026-10-25 isto estourava no
    // mount (achado 27 da revisão de 2026-10-24).
    try {
      const savedSource = window.localStorage.getItem(MENTIONS_SOURCE_KEY);
      if (savedSource === "brand" || savedSource === "publication") {
        setSourceMode(savedSource);
      }
    } catch {
      // Sem storage: fica a visão padrão.
    }
  }, []);

  const changeSourceMode = (mode: "brand" | "publication") => {
    setSourceMode(mode);
    try {
      window.localStorage.setItem(MENTIONS_SOURCE_KEY, mode);
    } catch {
      // Sem storage (janela privada, cota): a escolha vale só nesta aba.
    }
    // As duas visões contam coisas diferentes (menções vs publicações),
    // então a página 4 de uma não corresponde à página 4 da outra.
    setPage(1);
  };


  // A colagem vira uma lista guardada no servidor; o que fica na tela
  // é o número dela e o resultado. Lista sem NENHUM par ainda vira
  // filtro (vazio, que é a verdade), com o painel dizendo que nada
  // casou — melhor que fingir que não houve colagem.
  const applyPastedReferences = async (
    text: string = refDraft,
    inScope: MentionListScope = scope,
  ) => {
    setRefBusy(true);
    setRefError(null);
    try {
      const created = await apiClient.createMentionReferenceSet(
        token,
        text,
        inScope,
      );
      setRefFilter({
        setId: created.id,
        postCount: created.post_count,
        unmatched: created.unmatched,
        unmatchedCount: created.unmatched_count,
        expiresAt: Date.parse(created.expires_at),
        text,
        scope: inScope,
      });
      setPage(1);
    } catch (failure) {
      setRefError(
        errorMessage(failure, "Não foi possível resolver a lista colada."),
      );
    } finally {
      setRefBusy(false);
    }
  };

  const clearPastedReferences = () => {
    setRefFilter(null);
    setRefDraft("");
    setRefError(null);
  };


  // Lista colada vencida: quem detecta é o `load` da página (ele olha o
  // relógio antes de cada consulta); aqui está só o que ele faz.
  const expirePastedReferences = useCallback(() => {
    setRefFilter(null);
    setRefError("Esta lista colada expirou — cole de novo.");
  }, []);


  /** O catálogo carregado é do escopo anterior: a carga volta a ser
   *  devida. */
  const resetCatalog = () => {
    // Veículo não é catálogo por escopo (lista única do cliente) nem
    // vem da carga: o que a busca trouxe continua valendo.
    // A lista de clipadoras também é da empresa, não do escopo: fica,
    // pra o chip não perder o rótulo até a carga voltar.
    setFilterOptions((anterior) => ({
      ...EMPTY_FILTER_OPTIONS,
      vehicles: anterior.vehicles,
      channels: anterior.channels,
      clippingServices: anterior.clippingServices,
    }));
    setOptionsReady(false);
    setOptionsLoaded(false);
  };

  const changeScope = (next: MentionListScope) => {
    setScope(next);
    // Outro escopo é outra lista: a seleção (da página) e a página não
    // valem mais.
    setPage(1);
    // A lista colada procura os links no escopo em que foi resolvida;
    // num escopo mais largo, as publicações dos concorrentes ficariam
    // de fora. Resolver de novo é barato (a lista é atalho de 7 dias).
    // (Lista vinda da URL de um relatório não tem o texto: não há o que
    // resolver de novo.)
    if (refFilter && refFilter.text && refFilter.scope !== next) {
      void applyPastedReferences(refFilter.text, next);
    }
    // Marca, tema, grupo-tema e porta-voz são cadastro de CADA empresa:
    // o que estava escolhido no escopo anterior pode não existir neste,
    // e um nome sem id no catálogo novo sumiria da consulta deixando o
    // chip na tela. Como no formulário do relatório, trocar o escopo
    // limpa essas quatro e recarrega o catálogo do escopo novo.
    updateFilters(current => ({
      ...current,
      facets: {
        ...current.facets,
        ...Object.fromEntries(
          SCOPED_CATALOG_DIMENSIONS.map(dimension => [
            dimension,
            { values: new Set<string>(), exclude: false },
          ])
        ),
      },
    }));
    resetCatalog();
  };

  const changeOrder = (next: MentionOrder) => {
    setOrder(next);
    try {
      window.localStorage.setItem(MENTIONS_ORDER_KEY, next);
    } catch {
      // Sem storage (janela privada, cota): a escolha vale só nesta aba.
    }
    // Outra ordem é outra sequência de páginas.
    setPage(1);
  };

  // As marcas agora vêm do catálogo REAL — antes eram derivadas das
  // menções carregadas, então marca sem menção na página não aparecia
  // como opção de filtro.
  //
  // As marcas do ESCOPO (2026-10-05): com "Concorrentes" ou "Todas", as
  // dos concorrentes entram — ver `ownersForScope`.
  useEffect(() => {
    let cancelled = false;
    acrossOwners(ownersForScope(scope, competitorIds), client =>
      client.brands(token)
    )
      .then((result) => {
        if (!cancelled) setBrands(result);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [token, ownersKey]);

  // Debounce da busca: cada tecla vira requisição sem isso.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  /** As marcas no mesmo formato das outras dimensões de catálogo: a
   *  tela mostra nomes, a consulta manda ids. Marca não é cadastrada
   *  por marca (é a própria), então aqui o mapa é um-pra-um — mas o
   *  formato uniforme deixa o painel e os chips tratarem as seis
   *  dimensões de catálogo pelo mesmo caminho. */
  const brandCatalog = useMemo(() => byName(brands), [brands]);

  // (`savedIds`, na loja: os ids que um filtro SALVO trouxe, por
  // dimensão e nome (v3). A consulta os usa pra nome que o catálogo vivo
  // não conhece — tema renomeado ou desativado desde que o filtro foi
  // gravado. Mudam junto com `filters`, na mesma atualização.)

  /** As opções de cada dimensão. As de vocabulário fechado são
   *  constantes; as de catálogo vêm do servidor (carregadas na
   *  primeira abertura do painel) e já chegam dedupadas por nome. */
  const optionsFor = (dimension: Dimension) => {
    switch (dimension) {
      case "polarities":
        // Só as polaridades que a escala da empresa oferece: uma empresa
        // de 4 níveis não vê "Neutro" no filtro. O CHIP de um filtro
        // salvo com polaridade fora da escala continua legível, porque o
        // rótulo (abaixo, em `facetLabel`) lê o domínio inteiro.
        return [
          ...polarityOptions()
            .filter((option) => option.scales.includes(polarityScale))
            .map((option) => ({
              value: option.name,
              label: option.label,
            })),
          { value: NO_VALUE, label: "Sem classificação" },
        ];
      case "approaches":
        return APPROACH_CHOICES;
      case "sources":
        return SOURCE_CHOICES;
      case "brands":
        return brandCatalog.options;
      case "channels":
        return filterOptions.channels.options;
      case "vehicles":
        return filterOptions.vehicles.options;
      case "themes":
        // Uma entrada por GRUPO do nome (o tema é por nome; o mesmo nome
        // pode estar em grupos diferentes em marcas diferentes). A caixa
        // junta as repetições pelo `value`, então marcar numa marca nas
        // outras também; quem só procura o rótulo (`rotuloDe`) acha a
        // primeira e o resultado é o mesmo. O "Sem subtema" (2026-11-03)
        // fica no TOPO, fora de qualquer grupo.
        return [
          { value: NO_VALUE, label: "Sem subtema", top: true },
          ...filterOptions.themes.options.flatMap((option) => {
            const groups = filterOptions.groupsByTheme.get(option.value);
            return groups?.size
              ? [...groups].map((group) => ({ ...option, group }))
              : [option];
          }),
        ];
      case "themeGroups":
        return filterOptions.themeGroups.options;
      case "spokespersons":
        return filterOptions.spokespersons.options;
      case "keyMessages":
        // O "sem" é a PRIMEIRA pílula, como o "Sem subtema" (pedido da
        // dona, 2026-11-03; ficava no fim, como o "Sem classificação" da
        // polaridade).
        return [
          { value: NO_VALUE, label: "Sem mensagem-chave", top: true },
          ...filterOptions.keyMessages.options,
        ];
      case "publisherGroups":
        return filterOptions.publisherGroups.options;
      case "mediaTypes":
        return [...MEDIA_TYPE_CHOICES, { value: NO_VALUE, label: "Não informado" }];
      case "influencerTypes":
        return filterOptions.influencerTypes.options;
      case "clippingServices":
        return filterOptions.clippingServices;
      case "editors":
        return filterOptions.editors;
      case "tiers":
        return TIER_CHOICES;
      case "formats":
        return FORMAT_CHOICES;
    }
  };

  /** O rótulo de um valor selecionado — pro chip dizer "Protagonista"
   *  e não "protagonist". */
  const rotuloDe = (dimension: Dimension, value: string) =>
    optionsFor(dimension).find((option) => option.value === value)?.label ??
    value;

  /** Uma dimensão do recorte VIVO — o par valores + exclusão. */
  const facet = (dimension: Dimension) => filters.facets[dimension];
  /** A mesma dimensão no RASCUNHO do painel. Os controles de dentro do
   *  painel leem e escrevem aqui; chips, toolbar e consulta leem
   *  `facet`. */
  const draftFacet = (dimension: Dimension) => draft.facets[dimension];

  // As três mudanças de faceta são funções PURAS sobre um estado de
  // filtros: o painel as aplica ao rascunho, o resto ao vivo+rascunho.
  const withToggledFacet = (
    current: MentionFilters,
    dimension: Dimension,
    value: string
  ): MentionFilters => {
    const atual = current.facets[dimension];
    const values = new Set(atual.values);
    const removing = values.has(value);
    if (removing) values.delete(value);
    else values.add(value);
    const next = {
      ...current,
      facets: { ...current.facets, [dimension]: { ...atual, values } },
    };
    // Desmarcar um grupo de tema derruba os temas dele — ver
    // `dropThemesOfGroups`.
    if (dimension === "themeGroups" && removing) {
      return dropThemesOfGroups(next, [value], filterOptions.groupsByTheme);
    }
    return next;
  };

  const withFacetExclude = (
    current: MentionFilters,
    dimension: Dimension,
    exclude: boolean
  ): MentionFilters => ({
    ...current,
    facets: {
      ...current.facets,
      [dimension]: { ...current.facets[dimension], exclude },
    },
  });

  const withClearedFacet = (
    current: MentionFilters,
    dimension: Dimension
  ): MentionFilters => {
    const next = {
      ...current,
      facets: {
        ...current.facets,
        [dimension]: { values: new Set<string>(), exclude: false },
      },
    };
    // Limpar o grupo de tema limpa os temas dele junto (pedido da
    // galera de negócio, 2026-09-21) — ver `dropThemesOfGroups`.
    if (dimension === "themeGroups") {
      return dropThemesOfGroups(
        next,
        current.facets.themeGroups.values,
        filterOptions.groupsByTheme
      );
    }
    return next;
  };

  // Vivos (toolbar de polaridade e chips): valem na hora.
  const toggleFacet = (dimension: Dimension, value: string) =>
    updateFilters(current => withToggledFacet(current, dimension, value));
  const setFacetExclude = (dimension: Dimension, exclude: boolean) =>
    updateFilters(current => withFacetExclude(current, dimension, exclude));
  const clearFacet = (dimension: Dimension) =>
    updateFilters(current => withClearedFacet(current, dimension));

  // Do painel: só o rascunho muda, até o "Aplicar".
  const toggleDraftFacet = (dimension: Dimension, value: string) =>
    setDraft(current => withToggledFacet(current, dimension, value));
  const setDraftFacetExclude = (dimension: Dimension, exclude: boolean) =>
    setDraft(current => withFacetExclude(current, dimension, exclude));
  const clearDraftFacet = (dimension: Dimension) =>
    setDraft(current => withClearedFacet(current, dimension));

  /** Os valores de uma dimensão prontos pra consulta, ou `undefined`
   *  quando ela está sem filtro — o cliente omite o parâmetro. */
  const facetValues = (dimension: Dimension): string[] | undefined => {
    const { values } = filters.facets[dimension];
    return values.size ? [...values] : undefined;
  };


  /** O catálogo que traduz nome → ids de cada dimensão de id.
   *
   *  A tela guarda a seleção por NOME (decisão de 2026-08-27:
   *  homônimos entre marcas são UMA opção), e o servidor filtra por
   *  id. Um nome pode virar N ids, e é isso que atravessa. */
  const catalogFor = (dimension: Dimension) => {
    switch (dimension) {
      case "brands":
        return brandCatalog;
      case "vehicles":
        return filterOptions.vehicles;
      case "channels":
        return filterOptions.channels;
      case "influencerTypes":
        return filterOptions.influencerTypes;
      case "themes":
        return filterOptions.themes;
      case "themeGroups":
        return filterOptions.themeGroups;
      case "spokespersons":
        return filterOptions.spokespersons;
      case "keyMessages":
        return filterOptions.keyMessages;
      case "publisherGroups":
        return filterOptions.publisherGroups;
      default:
        return null;
    }
  };



  /** Nome → ids de uma dimensão de catálogo: o catálogo vivo, e os
   *  ids do filtro salvo pra nome que ele não resolve. É o MESMO
   *  resolvedor da consulta, do recorte do relatório e do salvar. */
  const resolveIds = (dimension: IdDimension, names: Iterable<string>): number[] =>
    resolveFacetIds(names, catalogFor(dimension), savedIds[dimension]);
  // O provedor da loja grava o filtro com o MESMO resolvedor da consulta.
  store.resolverRef.current = resolveIds;

  const facetIds = (dimension: IdDimension): number[] | undefined => {
    const { values, exclude } = filters.facets[dimension];
    if (!values.size) return undefined;
    return idsToSend(resolveIds(dimension, values), exclude);
  };

  /** A mensagem-chave tem o "Sem mensagem-chave" (`NO_VALUE`), que não é
   *  id e vai como `noKeyMessage`. Só ele escolhido: nenhum id. Um nome
   *  escolhido que não resolve vira `NO_MATCH_ID`, como nas outras. */
  const keyMessageQuery = () => {
    const { values, exclude } = filters.facets.keyMessages;
    const names = [...values].filter(value => value !== NO_VALUE);
    return {
      keyMessageIds: names.length
        ? idsToSend(resolveIds("keyMessages", names), exclude)
        : undefined,
      noKeyMessage: values.has(NO_VALUE) || undefined,
      excludeKeyMessages: exclude,
    };
  };

  /** O subtema tem o "Sem subtema" (`NO_VALUE`), que não é id e vai
   *  como `noTheme` — a mesma forma da mensagem-chave. */
  const themeQuery = () => {
    const { values, exclude } = filters.facets.themes;
    const names = [...values].filter(value => value !== NO_VALUE);
    return {
      themeIds: names.length
        ? idsToSend(resolveIds("themes", names), exclude)
        : undefined,
      noTheme: values.has(NO_VALUE) || undefined,
      excludeThemes: exclude,
    };
  };

  const buildQuery = useCallback((): MentionQuery => {
    // `localInstant` é o MESMO helper que a tela de relatório usa pra
    // montar a janela: é ele que faz "até 23:59" incluir o minuto
    // inteiro dos dois lados. Enquanto a conversão morava aqui, esta
    // tela e o relatório podiam discordar sobre o mesmo período — e a
    // promessa de "gerar relatório com este recorte" é justamente que
    // o total não muda no caminho.
    return {
      page,
      size: pageSize,
      order,
      q: debouncedQuery || undefined,
      q_in: searchInSent !== "all" ? searchInSent : undefined,
      // Cada dimensão vira uma lista + a flag de exclusão. As de
      // CATÁLOGO viajam por id: a tela mostra nomes (uma opção por
      // nome, mesmo que o cadastro tenha uma linha por marca), e a
      // tradução nome → ids acontece aqui, na borda.
      polarities: facetValues("polarities"),
      excludePolarities: facet("polarities").exclude,
      approaches: facetValues("approaches"),
      excludeApproaches: facet("approaches").exclude,
      postSources: facetValues("sources"),
      excludePostSources: facet("sources").exclude,
      channelIds: facetIds("channels"),
      excludeChannels: facet("channels").exclude,
      brandIds: facetIds("brands"),
      excludeBrands: facet("brands").exclude,
      vehicleIds: facetIds("vehicles"),
      excludeVehicles: facet("vehicles").exclude,
      // Inclusão pura: não há `exclude` pra mandar.
      clippingServices: facetValues("clippingServices"),
      tiers: facetValues("tiers"),
      excludeTiers: facet("tiers").exclude,
      formats: facetValues("formats"),
      excludeFormats: facet("formats").exclude,
      publisherTierIds: facetIds("influencerTypes"),
      excludePublisherTiers: facet("influencerTypes").exclude,
      ...themeQuery(),
      themeGroupIds: facetIds("themeGroups"),
      excludeThemeGroups: facet("themeGroups").exclude,
      spokespersonIds: facetIds("spokespersons"),
      excludeSpokespersons: facet("spokespersons").exclude,
      ...keyMessageQuery(),
      // O "Não informado" (`NO_VALUE`) é valor que o servidor aceita.
      mediaTypes: facetValues("mediaTypes"),
      excludeMediaTypes: facet("mediaTypes").exclude,
      publisherGroupIds: facetIds("publisherGroups"),
      excludePublisherGroups: facet("publisherGroups").exclude,
      // O valor já é o `sub`: não passa por catálogo.
      editorIds: facetValues("editors"),
      excludeEditors: facet("editors").exclude,
      discarded: filters.onlyDiscarded || undefined,
      reviewed:
        filters.reviewed === "all" ? undefined : filters.reviewed === "yes",
      titleCitation:
        filters.titleCitation === "all"
          ? undefined
          : filters.titleCitation === "yes",
      themeHighlight:
        filters.themeHighlight === "all"
          ? undefined
          : filters.themeHighlight === "yes",
      sharedPost:
        filters.sharedPost === "all" ? undefined : filters.sharedPost === "yes",
      edited: filters.edited === "all" ? undefined : filters.edited === "yes",
      dateFrom: localInstant(filters.dateFrom, filters.timeFrom, "start"),
      dateTo: localInstant(filters.dateTo, filters.timeTo, "end"),
      minIcena:
        showsCena && filters.minCena.trim() !== ""
          ? Number(filters.minCena)
          : undefined,
      // SEM gate de `showsCena`: valoração não é CENA — é fato da
      // planilha, e empresa que esconde a nota continua querendo
      // filtrar por valor publicitário.
      minAdValue: sanitizeMinAdValue(filters.minAdValue) ?? undefined,
      uploadIds: uploadFilter,
      referenceSetId: refFilter?.setId,
      scope: scope === "own" ? undefined : scope,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    pageSize,
    order,
    debouncedQuery,
    searchInSent,
    filters,
    filterOptions,
    brandCatalog,
    uploadFilter,
    refFilter,
    showsCena,
    scope,
    savedIds,
  ]);

  // Alguma dimensão que depende do catálogo está selecionada? Enquanto
  // estiver e o catálogo não tiver chegado, a consulta espera — ver
  // `CATALOG_DIMENSIONS`.
  const catalogFacetsSelected = useMemo(
    () =>
      CATALOG_DIMENSIONS.some((dimension) =>
        dimension === "themes"
          ? // Só o "Sem subtema" não precisa de catálogo (vai como flag).
            [...filters.facets.themes.values].some((value) => value !== NO_VALUE)
          : filters.facets[dimension].values.size > 0
      ) ||
      // Canal de filtro salvo ANTIGO (nomes, sem ids gravados): o nome só
      // vira id contra o catálogo, que chega com a primeira carga.
      [...filters.facets.channels.values].some(
        (name) =>
          !filterOptions.channels.idsByName.has(name) &&
          !savedIds.channels?.has(name)
      ),
    [filters, filterOptions.channels, savedIds]
  );
  // "Editado por" viaja por `sub`, então a consulta NÃO espera as
  // opções (não há nome→id a traduzir) — mas o chip precisa do nome,
  // e um filtro salvo com editor chega antes de o painel abrir. Só
  // dispara a carga; não entra no portão da consulta.
  const editorsSelected = filters.facets.editors.values.size > 0;


  // A chave do recorte: o texto da query que vai pro servidor. É ela, e
  // não a identidade de `load`, que dispara a consulta da página.
  const queryKey = useMemo(() => JSON.stringify(buildQuery()), [buildQuery]);


  // "Estou esperando o catálogo?" como UM booleano derivado, e não
  // `catalogFacetsSelected` e `optionsReady` soltos nas dependências
  // (2026-10-09): soltos, a chegada das opções do painel — que vira
  // `optionsReady` — rodava o efeito de novo mesmo sem nenhuma dimensão
  // de catálogo selecionada, e ABRIR o painel custava uma consulta
  // extra da lista, idêntica à anterior (medido no devstack). Derivado,
  // ele só muda quando a consulta estava de fato presa esperando.
  const waitingForCatalog = catalogFacetsSelected && !optionsReady;
  // O que a página enxerga como "ainda não dá pra consultar": o catálogo
  // OU a janela padrão da primeira visita (ver acima).
  const waitingForQuery = waitingForCatalog || waitingForWindow;



  // O catálogo das opções: seis rotas, uma vez só, na primeira vez que
  // o painel abre. Carregar no mount pagaria isso por quem nunca usa
  // filtro — que é a maioria das visitas à tela.
  useEffect(() => {
    // `catalogFacetsSelected` é a metade que faltava: um filtro SALVO
    // pode chegar com tema/grupo-tema/porta-voz/grupo de publicador sem
    // que o painel tenha aberto nenhuma vez. Antes disso, o catálogo
    // ficava vazio, `facetIds` devolvia `[]`, e a consulta saía SEM o
    // recorte — com o chip na tela dizendo que ele estava aplicado.
    if (
      (!filtersOpen && !optionsWanted && !catalogFacetsSelected && !editorsSelected) ||
      optionsLoaded
    )
      return;
    // Escopo restaurado que depende dos concorrentes: espera a lista.
    if (scope !== "own" && !competitorsLoaded) return;
    // **O descarte é por CARGA, não por vida do efeito** (conserto de
    // 2026-09-10). Antes era um `let alive = true` com `alive = false`
    // na limpeza, e ele se cancelava sozinho: `setOptionsLoaded(true)`
    // na linha seguinte muda uma DEPENDÊNCIA deste efeito, então o
    // React roda a limpeza da execução que acabou de começar — e a
    // resposta chegava com `alive` já falso. Resultado: `setFilterOptions`
    // NUNCA rodava. As seis dimensões de catálogo do painel ficavam
    // vazias (medido no devstack: Canal com 6 cadastrados, Grupo de
    // publicador com 40, e a tela mostrando "nenhuma opção"), e
    // `optionsReady` nunca virava true — o que é pior, porque é ele que
    // libera a consulta quando um filtro SALVO traz dimensão de
    // catálogo: a lista de menções esperava por um carimbo que não vinha.
    // É a mesma classe de bug que a entrega de 2026-08-30 fechou, viva
    // pelo outro lado.
    //
    // Com o contador, só uma carga NOVA invalida a anterior — que é o
    // que a guarda queria dizer.
    const minhaCarga = ++optionsRequestRef.current;
    setOptionsLoaded(true);
    void loadFilterOptions(token, scope, competitorIds).then((options) => {
      if (optionsRequestRef.current !== minhaCarga) return;
      // O catálogo de veículos CRESCE por busca e não vem desta carga:
      // trocar tudo apagaria o nome → ids do que a pessoa já marcou.
      // O de canais também: a busca o faz crescer além da primeira página.
      setFilterOptions((anterior) => ({
        ...options,
        vehicles: anterior.vehicles,
        channels: mergeNamedCatalog(options.channels, catalogRows(anterior.channels)),
      }));
      setOptionsReady(true);
    });
    // Os donos do catálogo entram pelo `changeScope`, que zera
    // `optionsLoaded` junto; estar aqui só faz o efeito ler os valores
    // de agora, e o `optionsLoaded` já carimbado o segura.
  }, [filtersOpen, optionsWanted, catalogFacetsSelected, editorsSelected, optionsLoaded, token, ownersKey, competitorsLoaded]);



  // Filtro novo com a página 4 aberta devolveria vazio sem isso.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, searchInSent, filters, pageSize, uploadFilter, refFilter]);



  // Conta DIMENSÕES ativas, derivado da lista — uma dimensão nova entra
  // no contador sozinha. Era o que faltava pro bug clássico do template
  // de referência ("o reset esquece um filtro, e ele nunca aparece como
  // ativo") não se repetir aqui.
  const activeFilterCount =
    DIMENSIONS.filter(
      dimension =>
        filters.facets[dimension].values.size > 0 &&
        (dimension !== "tiers" || showsCena || true)
    ).length +
    [
      filters.dateFrom || filters.dateTo || filters.timeFrom || filters.timeTo,
      showsCena && filters.minCena.trim() !== "",
      sanitizeMinAdValue(filters.minAdValue) !== null,
      uploadFilter.length > 0,
      refFilter !== null,
      filters.reviewed !== "all",
      filters.titleCitation !== "all",
      filters.themeHighlight !== "all",
      filters.sharedPost !== "all",
      filters.edited !== "all",
    ].filter(Boolean).length;

  // O rascunho do painel difere do que está valendo? Assinatura em vez
  // de comparação campo a campo: as facetas são `Set`, que `===` não
  // compara.
  const draftDiffers = filtersSignature(draft) !== filtersSignature(filters);



  // "Aplicar": promove o rascunho ao estado vivo (UMA consulta, não uma
  // por clique), volta pra página 1 (recorte novo com a página 4 aberta
  // devolveria vazio) e fecha. Rascunho igual ao vivo só fecha — trocar
  // a identidade de `filters` à toa zeraria a seleção de linhas.
  const applyDraft = () => {
    if (draftDiffers) {
      setFilters(draft);
      setPage(1);
    }
    setFiltersOpen(false);
  };


  // "Tentar de novo" do aviso de opções que não carregaram.
  const retryFilterOptions = () => {
    // Zera o "já disparei" — sem isso a carga não
    // acontece de novo nesta visita, e o painel fica
    // inútil até trocar de rota.
    setOptionsLoaded(false);
    setOptionsReady(false);
  };

  // Abrir semeia o rascunho do que está valendo agora. Fechar (pelo X
  // ou pelo botão) descarta o que não foi aplicado.
  const openPanel = () => {
    setDraft(filters);
    setFiltersOpen(true);
  };
  const closePanel = () => setFiltersOpen(false);


  // A lista de canais do painel é só a primeira página do cadastro
  // (`loadFilterOptions` pede 200). Se o cadastro é maior, a lista tem
  // que dizer que cortou — senão "Folha" ausente parece "não cadastrado".
  const channelsHint =
    "Canais de rede cadastrados. Recorta só a rede; a imprensa passa inteira.";
  const vehiclesHint =
    "Digite pra buscar no cadastro de veículos. Recorta só a imprensa; a rede passa inteira.";

  // Busca no servidor das duas caixas (2026-10-19). Identidade ESTÁVEL
  // (só o token): a caixa a tem nas dependências do efeito de busca, e
  // uma função nova a cada render a dispararia de novo a cada render.
  const searchVehicles = useCallback(
    async (texto: string) => {
      const pagina = await apiClient.listVehicles(token, { q: texto, size: 50 });
      // Funde ANTES de devolver: o id de um nome recém-buscado precisa
      // estar em `idsByName` quando a pessoa o marcar.
      setFilterOptions((anterior) => {
        const vehicles = mergeNamedCatalog(anterior.vehicles, pagina.items);
        return vehicles === anterior.vehicles ? anterior : { ...anterior, vehicles };
      });
      return byName(pagina.items).options;
    },
    [token]
  );
  const searchChannels = useCallback(
    async (texto: string) => {
      const pagina = await apiClient.listChannels(token, {
        q: texto,
        postSource: "network",
        size: 200,
      });
      // Funde ANTES de devolver, como o veículo: o id de um nome recém
      // buscado precisa estar em `idsByName` quando a pessoa o marcar.
      setFilterOptions((anterior) => {
        const channels = mergeNamedCatalog(anterior.channels, pagina.items);
        return channels === anterior.channels ? anterior : { ...anterior, channels };
      });
      return byName(pagina.items).options;
    },
    [token]
  );



  const applySavedFilter = (criteria: MentionFilterCriteria) => {
    // `brandNameById` existe pelos filtros salvos v1, que guardam a
    // marca por ID — o formato novo guarda o nome, como as outras
    // dimensões de catálogo.
    const next = stateFromCriteria(criteria, {
      showsCena,
      brandNameById: (id) => brands.find((brand) => brand.id === id)?.name,
      catalogs: {
        brands: brandCatalog,
        vehicles: filterOptions.vehicles,
        channels: filterOptions.channels,
        influencerTypes: filterOptions.influencerTypes,
        themes: filterOptions.themes,
        themeGroups: filterOptions.themeGroups,
        spokespersons: filterOptions.spokespersons,
        keyMessages: filterOptions.keyMessages,
        publisherGroups: filterOptions.publisherGroups,
      },
    });
    setShared(current => ({
      ...current,
      query: next.query,
      searchIn: next.queryIn ?? "all",
      filters: next.filters,
      savedIds: next.savedIds ?? {},
    }));
    setDraft(next.filters);
    // O recorte por upload não faz parte do filtro salvo — deixá-lo de
    // pé aqui daria um resultado diferente do que foi salvo.
    onChangeUploadFilter?.([]);
    setPage(1);
  };

  /** Troca TODO o recorte compartilhado por um recorte de relatório
   *  (criteria v3 + escopo + uploads). Como `applySavedFilter`, e mais:
   *  o escopo vem junto — se mudou, o catálogo carregado é de outro
   *  dono —, o filtro de upload é o do recorte, e a lista colada sai
   *  (os posts colados já viraram ids na foto; não há lista pra
   *  restaurar, e deixá-la de pé estreitaria o recorte sem o chip
   *  contar). */
  const applyReportCriteria = (
    criteria: MentionFilterCriteria,
    nextScope: MentionListScope,
    uploadIds: number[],
    refSet: PastedReferences | null = null
  ) => {
    const next = stateFromCriteria(criteria, { showsCena: true });
    setShared(current => ({
      ...current,
      query: next.query,
      searchIn: next.queryIn ?? "all",
      filters: next.filters,
      savedIds: next.savedIds ?? {},
      scope: nextScope,
      refFilter: refSet,
    }));
    setDraft(next.filters);
    if (nextScope !== scope) resetCatalog();
    onChangeUploadFilter?.(uploadIds);
    setPage(1);
  };

  /** "Editar recorte": carrega a criteria congelada de um relatório no
   *  filtro compartilhado (2026-10-17). Abrir o relatório NÃO faz isto —
   *  só este botão, de propósito. */
  const applyGenerationCriteria = (
    criteria: GenerationCriteria,
    labels: GenerationCriteriaLabels | null | undefined,
    fallbackBrandId: number | null
  ) => {
    applyReportCriteria(
      mentionCriteriaFromGeneration(criteria, labels, fallbackBrandId),
      criteria.scope ?? "own",
      criteria.upload_ids ?? []
    );
  };

  /** O recorte que veio na URL (`recorteFromSearch`), sem rótulos: as
   *  opções de catálogo entram como `#<id>` e ganham o nome quando o
   *  catálogo carrega. Quem chama decide QUANDO (só com o filtro
   *  compartilhado vazio). A lista colada volta como um filtro mínimo:
   *  só o número importa pra consulta e pro relatório. */
  const applyRecorte = (recorte: ReportRecorte) => {
    applyReportCriteria(
      mentionCriteriaFromRecorte(recorte),
      recorte.scope,
      recorte.uploadIds,
      recorte.referenceSet
        ? {
            setId: recorte.referenceSet.id,
            postCount: recorte.referenceSet.postCount ?? 0,
            unmatched: [],
            unmatchedCount: 0,
            expiresAt: Date.now() + REFERENCE_SET_TTL_MS,
            text: "",
            scope: recorte.scope,
          }
        : null
    );
  };

  // O recorte que atravessa pro relatório: TUDO, desde 2026-08-28.
  // `filters.minCena` e "sem classificação" ficam de fora de propósito
  // — ver o cabeçalho de `report-recorte.ts`.
  const reportRecorte = (): ReportRecorte => {
    const recorte = emptyRecorte();
    recorte.from = filters.dateFrom
      ? `${filters.dateFrom}T${filters.timeFrom || "00:00"}`
      : "";
    recorte.to = filters.dateTo
      ? `${filters.dateTo}T${filters.timeTo || "23:59"}`
      : "";
    recorte.q = query.trim();
    recorte.qIn = recorte.q ? searchIn : "all";

    for (const dimension of RECORTE_DIMENSIONS) {
      const origem = filters.facets[dimension.key];
      if (!origem.values.size) continue;
      // Nas dimensões de catálogo o que viaja é o ID; nas demais, o
      // próprio valor. O sentinela de "sem classificação" nunca viaja:
      // o relatório é foto do que já foi decidido — menos onde o
      // descritor diz que o "sem" é recorte (`noValueTravels`: o "Sem
      // mensagem-chave" e o tipo de mídia "Não informado").
      // Id: o mesmo `idsToSend` da consulta — um nome sem id não pode
      // virar um relatório MAIS LARGO do que o chip promete.
      const keepsNone = noValueTravels(dimension) && origem.values.has(NO_VALUE);
      const named = [...origem.values].filter(value => value !== NO_VALUE);
      const values = isIdDimension(dimension.key)
        ? [
            ...(named.length
              ? (idsToSend(resolveIds(dimension.key, named), origem.exclude) ?? []).map(String)
              : []),
            ...(keepsNone ? [NO_VALUE] : []),
          ]
        : [...named, ...(keepsNone ? [NO_VALUE] : [])];
      if (!values.length) continue;
      recorte.facets[dimension.key] = {
        values,
        exclude: origem.exclude,
      };
    }
    // Escalar, fora do laço das facetas — ver `report-recorte.ts`.
    recorte.minAdValue = sanitizeMinAdValue(filters.minAdValue);
    // A lista colada VIAJA (decisão da dona, 2026-10-02): "o relatório
    // destas matérias" é tarefa real. Vai o NÚMERO da lista; o servidor
    // a expande e a foto guarda os ids.
    recorte.reviewed =
      filters.reviewed === "all" ? null : filters.reviewed === "yes";
    recorte.titleCitation =
      filters.titleCitation === "all" ? null : filters.titleCitation === "yes";
    recorte.themeHighlight =
      filters.themeHighlight === "all" ? null : filters.themeHighlight === "yes";
    recorte.sharedPost =
      filters.sharedPost === "all" ? null : filters.sharedPost === "yes";
    // O escopo VIAJA (2026-10-04, relatório misto): gerar a partir da
    // lista de concorrentes dá o relatório deles.
    recorte.scope = scope;
    // O filtro de upload da URL viaja (2026-10-17): sem isto a página de
    // relatório abria como "empresa inteira".
    recorte.uploadIds = [...uploadFilter];
    recorte.referenceSet = refFilter
      ? { id: refFilter.setId, postCount: refFilter.postCount }
      : null;
    return recorte;
  };

  const polarityLabelOf = (name: string) =>
    name === NO_VALUE
      ? "Sem classificação"
      : (polarityOptions().find((option) => option.name === name)?.label ??
        name);

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (query.trim())
      chips.push({
        key: "query",
        label:
          `Busca: "${query.trim()}"` +
          (searchIn === "all"
            ? ""
            : ` · só no ${searchIn === "title" ? "título" : "conteúdo"}`),
        onRemove: () => {
          setQuery("");
          setSearchIn("all");
        },
      });
    // Um chip por DIMENSÃO, com os valores concatenados e o prefixo
    // "Exceto" quando o modo é de exclusão. Derivado da lista de
    // dimensões: uma dimensão nova aparece aqui sozinha.
    for (const dimension of DIMENSIONS) {
      const { values, exclude } = filters.facets[dimension];
      if (!values.size) continue;
      // Clipadora: um chip POR valor, cada um tira só o seu (inclusão
      // pura, então nunca há "exceto").
      if (dimension === "clippingServices") {
        for (const value of values) {
          chips.push({
            key: `clippingServices:${value}`,
            label: `${DIMENSION_LABELS[dimension]}: ${rotuloDe(dimension, value)}`,
            onRemove: () => toggleFacet(dimension, value),
          });
        }
        continue;
      }
      const rotulos = [...values].map((value) =>
        dimension === "polarities" ? polarityLabelOf(value) : rotuloDe(dimension, value)
      );
      chips.push({
        key: dimension,
        label: `${DIMENSION_LABELS[dimension]}: ${exclude ? "exceto " : ""}${rotulos.join(", ")}`,
        onRemove: () => clearFacet(dimension),
      });
    }
    if (filters.reviewed !== "all") {
      chips.push({
        key: "reviewed",
        label: filters.reviewed === "yes" ? "Só revisadas" : "Só não revisadas",
        onRemove: () =>
          updateFilters((current) => ({ ...current, reviewed: "all" })),
      });
    }
    if (filters.titleCitation !== "all") {
      chips.push({
        key: "titleCitation",
        label:
          filters.titleCitation === "yes"
            ? "Citação no título: com"
            : "Citação no título: sem",
        onRemove: () =>
          updateFilters((current) => ({ ...current, titleCitation: "all" })),
      });
    }
    if (filters.themeHighlight !== "all") {
      chips.push({
        key: "themeHighlight",
        label:
          filters.themeHighlight === "yes"
            ? "Subtema em destaque: com"
            : "Subtema em destaque: sem",
        onRemove: () =>
          updateFilters((current) => ({ ...current, themeHighlight: "all" })),
      });
    }
    if (filters.sharedPost !== "all") {
      chips.push({
        key: "sharedPost",
        label:
          filters.sharedPost === "yes"
            ? "Post em mais de um cliente"
            : "Post só neste cliente",
        onRemove: () =>
          updateFilters((current) => ({ ...current, sharedPost: "all" })),
      });
    }
    if (filters.edited !== "all") {
      chips.push({
        key: "edited",
        label: filters.edited === "yes" ? "Só editadas" : "Só não editadas",
        onRemove: () =>
          updateFilters((current) => ({ ...current, edited: "all" })),
      });
    }
    if (filters.onlyDiscarded) {
      chips.push({
        key: "discarded",
        label: "Só as descartadas",
        onRemove: () =>
          updateFilters((current) => ({ ...current, onlyDiscarded: false })),
      });
    }
    if (
      filters.dateFrom ||
      filters.dateTo ||
      filters.timeFrom ||
      filters.timeTo
    ) {
      chips.push({
        key: "period",
        label: "Período",
        onRemove: () =>
          updateFilters((current) => ({
            ...current,
            dateFrom: "",
            timeFrom: "",
            dateTo: "",
            timeTo: "",
          })),
      });
    }
    if (showsCena && filters.minCena.trim() !== "") {
      chips.push({
        key: "cena",
        label: `CENA ≥ ${filters.minCena}`,
        onRemove: () => updateFilters((current) => ({ ...current, minCena: "" })),
      });
    }
    const minAdValue = sanitizeMinAdValue(filters.minAdValue);
    if (minAdValue !== null) {
      chips.push({
        key: "adValue",
        // O chip mostra o valor SANEADO, não o digitado: se a pessoa
        // escreveu "1500,50", o que filtra é 1500, e o chip precisa
        // dizer o que está valendo.
        label: `Valoração ≥ ${formatCurrencyFull(minAdValue)}`,
        onRemove: () =>
          updateFilters((current) => ({ ...current, minAdValue: "" })),
      });
    }
    // Um chip POR upload, não um "3 selecionados": assim cada um sai
    // sozinho, sem ter que abrir o painel pra tirar um da lista.
    for (const id of uploadFilter) {
      // O upload pode não estar entre os carregados (lista limitada),
      // daí o `#id` como reserva — o chip nunca fica sem rótulo.
      chips.push({
        key: `upload-${id}`,
        label: `Upload: ${uploadLabelOf?.(id) ?? `#${id}`}`,
        onRemove: () =>
          onChangeUploadFilter?.(
            uploadFilter.filter((current) => current !== id),
          ),
      });
    }
    if (refFilter) {
      const semPar = refFilter.unmatchedCount;
      chips.push({
        key: "refs",
        label: `Links/códigos colados: ${refFilter.postCount}${
          semPar > 0 ? ` (${semPar} sem par)` : ""
        }`,
        onRemove: clearPastedReferences,
      });
    }
    return chips;
  }, [
    query,
    // O chip de busca diz ONDE ela procura; sem isto na lista, trocar o
    // seletor mudava a consulta e o chip ficava com o texto velho
    // (pego no devstack, mesma classe do `showsCena` abaixo).
    searchIn,
    filters,
    // `showsCena` estava faltando aqui — o chip de CENA mínimo não
    // sumia ao esconder a coluna até o próximo recálculo do memo.
    showsCena,
    brands,
    filterOptions,
    uploadFilter,
    uploadLabelOf,
    onChangeUploadFilter,
    refFilter,
  ]);

  // Opção de catálogo que entrou como `#<id>` (recorte vindo da URL, sem
  // rótulo) ganha o nome de HOJE quando o catálogo chega. Só troca o
  // nome que o catálogo conhece pelos ids guardados; o que ele não
  // conhece (desativado) fica `#<id>`, que ao menos diz o que é.
  useEffect(() => {
    const trocas: Array<[IdDimension, string, string]> = [];
    for (const dimension of ID_DIMENSIONS) {
      const catalog = catalogFor(dimension);
      if (!catalog?.nameById.size) continue;
      for (const value of filters.facets[dimension].values) {
        if (!PROVISIONAL_LABEL.test(value)) continue;
        const ids = savedIds[dimension]?.get(value) ?? [];
        const nome = ids
          .map((id) => catalog.nameById.get(id))
          .find((candidate): candidate is string => Boolean(candidate));
        if (nome) trocas.push([dimension, value, nome]);
      }
    }
    if (!trocas.length) return;
    const renomear = (current: MentionFilters): MentionFilters => {
      const facets = { ...current.facets };
      for (const [dimension, antigo, novo] of trocas) {
        const values = new Set(facets[dimension].values);
        if (!values.delete(antigo)) continue;
        values.add(novo);
        facets[dimension] = { ...facets[dimension], values };
      }
      return { ...current, facets };
    };
    setShared((current) => {
      const guardados: SavedFacetIds = { ...current.savedIds };
      for (const [dimension, antigo, novo] of trocas) {
        const mapa = new Map(guardados[dimension] ?? []);
        const ids = mapa.get(antigo);
        if (!ids) continue;
        mapa.delete(antigo);
        mapa.set(novo, [...new Set([...(mapa.get(novo) ?? []), ...ids])]);
        guardados[dimension] = mapa;
      }
      return {
        ...current,
        filters: renomear(current.filters),
        savedIds: guardados,
      };
    });
    setDraft(renomear);
  }, [optionsReady, brands]);

  // Os botões de período da barra da lista (2026-10-26). O ativo é o que
  // CASA com as datas de agora: com o último dado conhecido, a janela de
  // N dias que termina nele; sem ele (a pessoa voltou com o filtro já
  // guardado e nada foi buscado), a janela de N dias que termina onde
  // terminar — não dá pra conferir o último dado sem ir ao servidor, e
  // abrir a tela custaria uma chamada a mais por isso. `null` é o
  // "Personalizado": janela digitada à mão que não casa nenhum botão, ou
  // nenhuma data (a pessoa limpou o período no painel). Não existe botão
  // "tudo" (decisão da dona: ver a empresa inteira não pode ser um clique
  // só); quem quer, abre o painel e esvazia as datas.
  const activePeriod: SummaryPeriod | null = (() => {
    if (filters.timeFrom || filters.timeTo || !filters.dateFrom || !filters.dateTo)
      return null;
    for (const days of SUMMARY_PERIODS) {
      if (
        filters.dateFrom === shiftDays(filters.dateTo, -(days - 1)) &&
        (latestDay === undefined || filters.dateTo === latestDay)
      )
        return days;
    }
    return null;
  })();

  /** Commita a janela de `days` dias terminando no último dado, e lembra
   *  a escolha pra Início e Visão geral baterem. */
  const choosePeriod = async (days: SummaryPeriod) => {
    setSummaryDays(days);
    const day = await fetchLatestDay();
    if (day === null) return;
    updateFilters((current) => ({ ...current, ...windowOf(day, days) }));
    setPage(1);
  };

  /** "Limpar filtros": tudo vazio, MENOS o período, que volta pra janela
   *  padrão (o período escolhido, relativo ao último dado). Se a rota
   *  falhar, limpa sem janela. */
  const clearFilters = async () => {
    const day = await fetchLatestDay();
    commitFilters(
      day === null
        ? emptyFilters()
        : { ...emptyFilters(), ...windowOf(day, summaryDaysRef.current) }
    );
  };

  return {
    query,
    setQuery,
    searchIn,
    setSearchIn,
    debouncedQuery,
    filters,
    setFilters,
    draft,
    setDraft,
    updateFilters,
    commitFilters,
    summaryDays,
    activePeriod,
    choosePeriod,
    clearFilters,
    facet,
    draftFacet,
    toggleFacet,
    setFacetExclude,
    clearFacet,
    toggleDraftFacet,
    setDraftFacetExclude,
    clearDraftFacet,
    resolveIds,
    optionsFor,
    rotuloDe,
    filterOptions,
    optionsLoaded,
    optionsReady,
    retryFilterOptions,
    requestFilterOptions,
    catalogFacetsSelected,
    editorsSelected,
    waitingForCatalog: waitingForQuery,
    filtersOpen,
    setFiltersOpen,
    openPanel,
    closePanel,
    applyDraft,
    draftDiffers,
    channelsHint,
    vehiclesHint,
    searchVehicles,
    searchChannels,
    activeFilterCount,
    brands,
    scope,
    changeScope,
    competitorIds,
    hasCompetitors,
    order,
    changeOrder,
    sourceMode,
    changeSourceMode,
    page,
    setPage,
    pageSize,
    setPageSize,
    refFilter,
    refDraft,
    setRefDraft,
    refBusy,
    refError,
    applyPastedReferences,
    clearPastedReferences,
    expirePastedReferences,
    buildQuery,
    queryKey,
    applySavedFilter,
    applyGenerationCriteria,
    applyRecorte,
    reportRecorte,
    activeChips,
  };
}
